import $ from 'jquery'
import _ from 'lodash'
import KeyCodes from 'scoreboard/components/key-codes'
const { ESC } = KeyCodes
import Backbone from 'backbone'

/**
 * The dialogStack works as a manager for all of our dialogs. When a new dialog is
 * added via the app-display (appDisplay.dialog(<view>)), it is pushed into this.dialogs and
 * rendered onto the screen in the correct order.
 * All adding and removal of dialog markup is done through the dialog-stack.
 *
 * There are two ways a dialog can be closed:
 * 1. When a view within a dialog triggers a 'close', 'cancel', or 'done' event, that
 *    dialog calls its close() method and triggers it's 'close' event which this dialog-stack
 *    listens for in order to fully remove that dialog's markup and adjust this.dialogs.
 * 2. When the ESC key is hit, the dialog-stack calls the escape() function on the topmost
 *    dialog which, in turn, lets the view it's displaying know it's time to close. When that view closes/triggers it's
 *    'close' event, closing method #1 (above) kicks in.
 */

var dialogStack = {
	dialogs: [],

	push: function (dialog) {
		// Unbind any listeners so that we can rebind on the new dialog
		this._unbindEvents()

		this._bindEvents()

		// Need to add dialog to the DOM so we get accurate dimensions
		// it won't be visible until 'open' class is added
		if ($('.dialog-mask').length > 0) {
			// Some other dialog exists, we'll create a new mask
			$('.dialog-mask').remove()
			$('.dialog-stack').append('<span class="dialog-mask open">&nbsp;</span>')
		} else {
			// no other dialog exists, so we'll create a mask just for this one
			$('.dialog-stack').append('<span class="dialog-mask">&nbsp;</span>')
		}

		// When covering up an existing dialog, make sure to dismiss the marked
		// field tooltips lest they linger.
		// https://github.com/SpiderStrategies/Scoreboard/issues/17126#issuecomment-416599282
		var previousDialog = this.peek()
		if (previousDialog) {
			// wait an extra tick for the tooltip to be shown
			// (tooltip particularly late on mobile safari iOS > 11)
			process.nextTick(() => previousDialog.removeMarkedTooltips())
		}

		dialog.on('open', () => {
			$('.dialog-mask').addClass('open')
			// setting the scroll top for safari. If this is not set then the body
			// is shown scrolled up after the dialog is opened. This doesn't seem to
			// be a problem for any other platform. #thankssafari
			$('body')[0].scrollTop = '0'
		})

		$('.dialog-stack').append(dialog.el)
		dialog.render()

		dialog.on('done close', () => {
			this.remove(dialog)
			// Only transition the dialog-mask opacity if the last open dialog
			// just closed.  #12946 this has to be after the transition
			if ($('.dialog').length < 1) {
				$('.dialog-mask').remove()
				$('body').removeClass('is-dialog-visible')
				Backbone.trigger('empty:dialogStack')
			}

			$('body').removeClass('is-dialog-closing')
		})

		this.dialogs.push(dialog)
		$('body').toggleClass('is-dialog-visible', true)
	},

	pop: function () {
		return this.remove(this.peek())
	},

	// removes the given dialog
	remove: function(dialog) {
		if (this.dialogs.length == 0) {
			return null
		}

		// Unbind any listeners; if there is still another dialog in the stack,
		// we'll rebind later on that
		this._unbindEvents()

		_.remove(this.dialogs, function(d) {
			return d == dialog
		})

		$('body').addClass('is-dialog-closing')
		// Move dialog-mask under the last open dialog and on top of the others if there are any.
		let numDialogs = this.dialogs.length
		if (numDialogs) {
			this.dialogs[numDialogs - 1].$el.before($('.dialog-mask'))
			this._bindEvents()
		}

		if (numDialogs == 0) {
			$('body').removeClass('is-dialog-visible')
			Backbone.trigger('empty:dialogStack')
		}

		$('body').removeClass('is-dialog-closing')
		return dialog
	},

	peek: function (opt = { ignoreAlerts: false }) {
		let filteredDialogs = this.dialogs.filter(d => {
			if (opt.ignoreAlerts) {
				return d.$('.is-alert-popup').length === 0
			}
			return true
		})

		if (filteredDialogs.length == 0) {
			return null
		}

		return filteredDialogs.slice(-1)[0]
	},

	/**
	 * If the top dialog view is of the provided type the view instance is returned.
	 * @param {View} viewType A backbone View Class
	 */
	topViewByType: function(viewType) {
		const topDialog = this.peek()
		const topView = _.get(topDialog, '_content')
		if (topView instanceof viewType) {
			return topView
		}
	},

	/**
	 * Returns the first dialog that matches the predicate from the stack of dialogs
	 *
	 * @param {Function} predicate provided a dialog on the current stack
	 *
	 * @return {dialog, view} The dialog and the content view it contains
	 */
	findDialogWithView: function(predicate) {
		const dialog = _.find(this.dialogs, d => {
			return predicate(d._content)
		})
		return dialog ? {dialog, view: dialog._content} : {}
	},

	/**
	 * @returns {Boolean} if any of the views in the stack are of the given type
	 */
	hasViewType: function(viewType) {
		return !_.isNil(this.findDialogWithView(v => v instanceof viewType).view)
	},

	clear: function () {
		let removed = this.pop()
		while (removed) {
			removed.close()
			removed = this.pop()
		}
	},

	/**
	 * Determine whether there is a dialog visible or not.
	 * @return {Boolean} True if there is a dialog showing, else false.
	 */
	isDialogVisible() {
		return $('body.is-dialog-visible').length > 0
	},

	_bindEvents: function () {
		$(document).on('keyup.dialog-stack', this._onKeyUp.bind(this))
	},

	_unbindEvents: function () {
		$(document).off('keyup.dialog-stack')
	},

	_onKeyUp: function (e) {
		if (e.keyCode == ESC) {
			// we decided users should be able to escape out of dialogs
			// even if the server is in the middle of saving something
			var top = this.peek()
			if (top) {
				top.escape()
			}
		}
	}
}

export default dialogStack

dialogStack.__filename__ = 'components/dialog-stack.js'
